// import API from "../lib/axios";
import { LoginResponse } from "../types/auth";
import API_ENTRY from "../lib/axiosEntry";
import { logoutCompletely } from "../utils/logoutCompletely";

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";

let logoutTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleAutoLogout(accessToken: string) {
  const payload = JSON.parse(atob(accessToken.split(".")[1]));
  const expiresAt = payload.exp * 1000;
  const delay = expiresAt - Date.now();

  if (delay <= 0) {
    logoutCompletely();
    return;
  }

  if (logoutTimer) clearTimeout(logoutTimer);
  logoutTimer = setTimeout(() => {
    console.warn("⏰ Token expired — auto-logout triggered");
    logoutCompletely();
  }, delay);
}

export const login = async (
  username: string,
  password: string
): Promise<void> => {
  try {
    const response = await API_ENTRY.post<LoginResponse>("/auth/access", {
      // const response = await API.post<LoginResponse>("/entry/auth/access", {
      username,
      password,
    });
    const { accessToken, refreshToken } = response.data;

    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    sessionStorage.removeItem("sessionExpired");

    scheduleAutoLogout(accessToken);
  } catch (error: any) {
    if (error.response) {
      const { status } = error.response;
      if (status === 401) {
        console.error("🔒 Invalid credentials (401)");
      } else if (status === 400) {
        console.error("📛 Bad request (400) — check payload");
      } else {
        console.error(`❌ Unexpected error (${status}):`, error.response.data);
      }
    } else {
      console.error("❌ Network or unknown error:", error.message);
    }
    throw error;
  }
};

export const refreshToken = async (): Promise<string> => {
  const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!storedRefreshToken) {
    console.warn("⚠️ No refresh token found — logging out");
    logoutCompletely();
    throw new Error("No refresh token");
  }

  try {
    const response = await API_ENTRY.post<LoginResponse>("/auth/refresh", {
      // const response = await API.post<LoginResponse>("/entry/auth/refresh", {
      refreshToken: storedRefreshToken,
    });
    const { accessToken: newAccessToken } = response.data;

    localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
    console.info("🔄 Token refreshed successfully");
    scheduleAutoLogout(newAccessToken);
    return newAccessToken;
  } catch (error: any) {
    if (error.response?.status === 401) {
      console.error("🔒 Refresh token invalid or expired (401)");
    } else {
      console.error("❌ Token refresh failed:", error);
    }
    logoutCompletely();
    throw error;
  }
};

export const verifyToken = async (accessToken: string): Promise<void> => {
  try {
    await API_ENTRY.post("/auth/verification", { accessToken });
    // await API.post("/entry/auth/verification", { accessToken });
  } catch (error: any) {
    if (error.response?.status === 401) {
      console.warn("🔒 Token is invalid (401)");
    } else {
      console.error("❌ Token verification failed:", error);
    }
    logoutCompletely();
    throw error;
  }
};
